/**
 * Os onze cadastros que sustentam o conteúdo, numa tela só.
 *
 * É o resto do menu Conteúdo do sistema antigo — produto, assunto, marca,
 * veículo, versão, motor, origem, sistema, tipo de informação, tipo e categoria
 * de veículo. Todos têm o mesmo CRUD, então a tela é uma só e os campos vêm do
 * `esquemas`.
 *
 * **Dois cadastros têm mais do que campos.** O veículo carrega a categoria, que
 * é metade da regra de acesso do cliente — e por isso a lista dele avisa quando
 * há veículo fora de todo contrato. Assunto e sistema têm a chave composta com o
 * idioma, e ganham o seletor Pt/Es: sem ele o espanhol ficaria invisível, e o
 * mesmo id existe nas duas bases com nomes diferentes.
 *
 * **Tudo aqui é dado global.** Renomear um assunto renomeia em todo o conteúdo
 * publicado, e apagar um que está em uso é o tipo de coisa que só aparece
 * depois, no site do cliente. Por isso a exclusão pede confirmação com o alcance
 * escrito, e em desenvolvimento a trava só deixa passar registro marcado com o
 * prefixo `ZZTESTE` — criar e apagar o próprio teste, nunca encostar no resto.
 */

import { useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, deTeste, foraDoProjeto, obter } from "@/api/cliente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { catalogoPorProjeto } from "@/api/query"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import {
  ordenarLista, SeletorDeOrdem, usarOrdemDaLista, usarRecemIncluido, usarRolarAteRecente,
} from "@/kit/ordemDaLista"
import { recalcularDescricao, type RecalculoDeDescricao } from "@/api/cobertura"
import { ESQUEMAS, esquemaDe, esteEsta, type Campo, type Esquema } from "./esquemas"
import { MigrarCadastro } from "./MigrarCadastro"
import { AlcanceDoVeiculo } from "./AlcanceDoVeiculo"
import { ImagensDoVeiculo } from "./ImagensDoVeiculo"
import { VeiculosSemCategoria } from "./VeiculosSemCategoria"

/** O mesmo corte do resto do sistema: desenha 200 e diz quanto escondeu. */
const DESENHADOS = 200

type Registro = Record<string, unknown>

/**
 * Resolve os ids de referência para nome.
 *
 * Sem isso a coluna Marca do cadastro de veículos mostra "18", e ninguém procura
 * um veículo pelo número da marca. Os catálogos referenciados são pequenos
 * (73 marcas, 1.202 veículos) e carregam uma vez por sessão, então resolver aqui
 * custa uma chamada — o contrário seria uma por linha.
 */
function usarNomesDeReferencia(esquema: Esquema) {
  const usadas = [...new Set(
    esquema.campos.filter((c) => c.tipo === "referencia" && c.referencia).map((c) => c.referencia!))]

  const marcas = useQuery({
    queryKey: ["catalogo-conteudo", "marcas"] as const,
    enabled: usadas.includes("marcas"),
    ...catalogoPorProjeto,
    queryFn: () => obter<Registro[]>("api/marcas", {}),
  })
  const veiculos = useQuery({
    queryKey: ["catalogo-conteudo", "veiculos"] as const,
    enabled: usadas.includes("veiculos"),
    ...catalogoPorProjeto,
    queryFn: () => obter<Registro[]>("api/veiculos", {}),
  })

  return useMemo(() => {
    const mapa = new Map<string, Map<number, string>>()
    const montar = (linhas: Registro[] | undefined) =>
      new Map((linhas ?? []).map((x) => [
        Number(x.id),
        String(x.nome ?? x.modelo ?? x.descricao ?? x.id),
      ]))
    mapa.set("marcas", montar(marcas.data))
    mapa.set("veiculos", montar(veiculos.data))
    return mapa
  }, [marcas.data, veiculos.data])
}

export function CadastrosDeConteudo() {
  const qc = useQueryClient()
  const [chave, setChave] = useState(ESQUEMAS[0]!.chave)
  const [base, setBase] = useState(1)
  const [busca, setBusca] = useState("")
  const [escolhido, setEscolhido] = useState<Registro | null>(null)
  const [incluindo, setIncluindo] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)
  const [ordem, setOrdem] = usarOrdemDaLista("cadastros-conteudo")
  const recente = usarRecemIncluido()
  const rolagem = useRef<HTMLDivElement>(null)

  const esquema = esquemaDe(chave)
  const nomes = usarNomesDeReferencia(esquema)

  // Só quem tem chave composta carrega a base; nos outros o parâmetro não existe
  // na rota, e mandá-lo seria pedir para a API ignorar algo em silêncio.
  const porBase = esquema.porBase === true
  const params = porBase ? { baseId: base } : {}
  const sufixo = porBase ? `?baseId=${base}` : ""

  const lista = useQuery({
    queryKey: ["cadastro-conteudo", chave, porBase ? base : 0] as const,
    queryFn: () => obter<Registro[]>(esquema.rota, params),
  })

  const excluir = useMutation({
    // O DELETE não tem corpo, então a trava não tem de onde ler o nome do
    // registro — e sem ele recusaria até o ZZTESTE que ela mesma deixou criar.
    // A tela tem o registro na mão; então ela declara. Em produção, onde a trava
    // não existe, o header é ignorado e some antes de ir para a rede.
    mutationFn: async ({ id, nome }: { id: number; nome: string }) => {
      await api.delete(`${esquema.rota}/${id}${sufixo}`, deTeste(nome, foraDoProjeto()))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cadastro-conteudo", chave] })
      // Os catálogos da tela de Conteúdo leem as mesmas rotas.
      qc.invalidateQueries({ queryKey: ["catalogo-conteudo"] })
      setEscolhido(null)
    },
  })

  function trocar(nova: string) {
    setChave(nova)
    setBase(1)
    setBusca("")
    setEscolhido(null)
    setIncluindo(false)
    setRecado(null)
    recente.marcar(null)
  }

  /** Vem do aviso de "sem categoria": acha a linha e abre a ficha dela. */
  function escolherPorId(id: number) {
    const achado = (lista.data ?? []).find((r) => Number(r.id) === id)
    if (!achado) return
    setEscolhido(achado)
    setIncluindo(false)
    setBusca("")
  }

  const achados = useMemo(() => {
    const todos = (lista.data ?? []).filter((r) =>
      esquema.zeroEhReal ? true : Number(r.id) > 0)
    const t = busca.trim().toLowerCase()
    const filtrados = !t ? todos : todos.filter((r) =>
      esquema.campos.some((c) => textoDe(r[c.chave], c, nomes).toLowerCase().includes(t)))
    // O nome é a primeira coluna da grade — é por ela que a pessoa procura com o olho.
    const principal = esquema.campos.find((c) => c.naGrade) ?? esquema.campos[0]!
    return ordenarLista(filtrados, ordem, (r) => Number(r.id),
      (r) => textoDe(r[principal.chave], principal, nomes))
  }, [lista.data, busca, esquema, nomes, ordem])

  const corte = achados.slice(0, DESENHADOS)
  // Na ordem alfabética o recém-incluído pode cair depois do corte; aí ele sobe para o
  // topo enquanto está realçado, em vez de sumir.
  const fora = recente.id != null && !corte.some((r) => Number(r.id) === recente.id)
    ? achados.find((r) => Number(r.id) === recente.id) : undefined
  const desenhados = fora ? [fora, ...corte.slice(0, DESENHADOS - 1)] : corte
  const naGrade = esquema.campos.filter((c) => c.naGrade)
  usarRolarAteRecente(rolagem, recente.id, desenhados)

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Cadastros do conteúdo"
        detalhe={lista.data ? `${achados.length.toLocaleString("pt-BR")} ${esquema.plural.toLowerCase()}` : undefined}
      >
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={`Filtrar ${esquema.plural.toLowerCase()}…`}
          style={{
            padding: "4px 9px", fontSize: 12, width: 220,
            border: "1px solid var(--line)", borderRadius: 6,
            background: "var(--surface)", color: "var(--txt)",
          }}
        />
        {esquema.porBase && (
          <select
            value={base}
            onChange={(e) => { setBase(Number(e.target.value)); setEscolhido(null); setIncluindo(false) }}
            title="Idioma do registro — a chave deste cadastro inclui a base"
            style={{
              padding: "4px 7px", fontSize: 12,
              border: "1px solid var(--line)", borderRadius: 6,
              background: "var(--surface)", color: "var(--txt)",
            }}
          >
            <option value={1}>Português</option>
            <option value={2}>Espanhol</option>
          </select>
        )}
        <SeletorDeOrdem valor={ordem} aoMudar={setOrdem} estilo={{ height: "auto", padding: "4px 7px", borderRadius: 6 }} />
        {/* Com a ficha nova aberta, "Incluir" lá em cima parecia o botão de
            gravar — e quem clicava achando que gravou perdia o preenchido. */}
        {!incluindo && (
          <Botao aoClicar={() => { setIncluindo(true); setEscolhido(null) }} destaque>
            Incluir
          </Botao>
        )}
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda={`${esquema.plural} valem para todo o conteúdo publicado`} />

      {/* O unico cadastro em que faltar um vinculo tranca conteudo publicado. */}
      {chave === "veiculos" && <VeiculosSemCategoria aoEscolher={escolherPorId} />}

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <nav style={{
          flex: "0 0 210px", width: 210, overflowY: "auto", padding: 10,
          borderRight: "1px solid var(--line2)",
        }}>
          {ESQUEMAS.map((e) => (
            <button
              key={e.chave}
              onClick={() => trocar(e.chave)}
              style={{
                display: "block", width: "100%", textAlign: "left", marginBottom: 2,
                border: 0, borderRadius: 5, padding: "5px 8px", cursor: "pointer", fontSize: 12,
                background: chave === e.chave ? "var(--acao-soft)" : "transparent",
                color: chave === e.chave ? "var(--acao-on-soft)" : "var(--txt)",
                fontWeight: chave === e.chave ? 600 : 400,
              }}
            >
              {e.plural}
              <span style={{ float: "right", fontSize: 10, color: "var(--txt3)" }}>{e.medido}</span>
            </button>
          ))}
        </nav>

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "9px 14px", borderBottom: "1px solid var(--line2)" }}>
            <p style={{ margin: 0, fontSize: 11.5, color: "var(--txt2)" }}>{esquema.oQueE}</p>
            {esquema.ressalva && (
              <p style={{ margin: "5px 0 0", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5 }}>
                {esquema.ressalva}
              </p>
            )}
          </div>

          <div ref={rolagem} style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            {lista.isPending ? (
              <p style={{ padding: 14, fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
            ) : lista.error ? (
              <p style={{ padding: 14, fontSize: 12.5, color: "var(--erro)" }}>
                {lista.error instanceof ErroDaApi ? lista.error.message : "Não foi possível ler."}
              </p>
            ) : achados.length === 0 ? (
              <p style={{ padding: 14, fontSize: 12.5, color: "var(--txt3)" }}>
                {busca ? "Nada com esse texto." : `Nenhum ${esquema.singular} cadastrado.`}
              </p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ position: "sticky", top: 0, background: "var(--surface)", zIndex: 1 }}>
                    <th style={{ ...th, width: 80 }}>Id</th>
                    {naGrade.map((c) => (
                      <th key={c.chave} style={{ ...th, width: c.largura }}>{c.rotulo}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {desenhados.map((r) => (
                    <tr
                      key={String(r.id)}
                      onClick={() => { setEscolhido(r); setIncluindo(false) }}
                      data-recem-incluido={recente.id != null && Number(r.id) === recente.id ? "" : undefined}
                      style={{
                        cursor: "pointer", borderBottom: "1px solid var(--line2)",
                        background: escolhido?.id === r.id ? "var(--acao-soft)" : undefined,
                      }}
                    >
                      <td className="mono" style={td}>{String(r.id)}</td>
                      {naGrade.map((c) => (
                        <td key={c.chave} style={{ ...td, maxWidth: c.largura ?? 300 }}
                            title={textoDe(r[c.chave], c, nomes)}>
                          <span style={{
                            display: "block", overflow: "hidden",
                            textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {textoDe(r[c.chave], c, nomes)}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {achados.length > desenhados.length && (
            <p style={{
              flex: "0 0 auto", margin: 0, padding: "6px 14px", fontSize: 11,
              color: "var(--txt3)", borderTop: "1px solid var(--line2)",
            }}>
              {achados.length.toLocaleString("pt-BR")} encontrados; mostrando {DESENHADOS}.
              Use o filtro para estreitar.
            </p>
          )}
        </div>

        {(escolhido || incluindo) && (
          <Formulario
            // O formulário guarda o que foi digitado; sem a chave, trocar de
            // linha (ou abrir a cópia recém-duplicada) mostraria a ficha anterior.
            key={incluindo ? "novo" : String(escolhido?.id)}
            esquema={esquema}
            base={base}
            registro={incluindo ? null : escolhido}
            aoFechar={() => { setEscolhido(null); setIncluindo(false) }}
            aoAvisar={setRecado}
            aoIncluir={(id) => {
              // Um filtro digitado antes poderia esconder justamente o que acabou de entrar.
              setBusca("")
              recente.marcar(id)
            }}
            aoDuplicar={(copia, id) => {
              setBusca("")
              recente.marcar(id)
              // Abre a cópia: o próximo passo é ajustar o que difere e gravar.
              if (copia?.id != null) setEscolhido(copia)
              else setEscolhido(null)
            }}
            aoExcluir={(id) => {
              setRecado(null)
              excluir.mutate({ id, nome: String(escolhido?.nome ?? "") }, {
                onSuccess: () => setRecado(`${maiuscula(esquema.singular)} ${id} excluído.`),
                onError: (e) => setRecado(mensagem(e)),
              })
            }}
            excluindo={excluir.isPending}
          />
        )}
      </div>

      <BarraDeEstado>{recado ?? ""}</BarraDeEstado>
    </div>
  )
}

// ---------------------------------------------------------------- formulário

function Formulario({ esquema, base, registro, aoFechar, aoAvisar, aoIncluir, aoDuplicar, aoExcluir, excluindo }: {
  esquema: Esquema
  /** Idioma corrente, quando o cadastro tem chave composta. Ver `porBase`. */
  base: number
  registro: Registro | null
  aoFechar: () => void
  aoAvisar: (m: string | null) => void
  /** Com o id que o POST devolveu — é ele que a lista realça. */
  aoIncluir: (id: number | null) => void
  /** Com a cópia já gravada, para a tela abri-la no lugar do original. */
  aoDuplicar: (copia: Registro | null, id: number | null) => void
  aoExcluir: (id: number) => void
  excluindo: boolean
}) {
  const qc = useQueryClient()
  const [valores, setValores] = useState<Registro>(() => registro ? { ...registro } : {})
  const [confirmando, setConfirmando] = useState<"excluir" | "duplicar" | null>(null)

  /**
   * Duplicar grava um registro novo com o que está na ficha agora — inclusive o
   * que foi mudado e não gravado, que não vai para o original. É para quem quer
   * um cadastro parecido sem preencher do zero; o nome repetido fica para a
   * pessoa ajustar na cópia, que já abre em seguida.
   */
  const duplicar = useMutation({
    mutationFn: async (dados: Registro) => {
      const corpo: Registro = { ...dados }
      delete corpo.id
      const r = await api.post(esquema.rota, esquema.porBase ? { ...corpo, baseId: base } : corpo,
                               foraDoProjeto())
      // O POST pode devolver só parte dos campos; o que faltar vem do que foi enviado.
      return { ...corpo, ...((r.data ?? {}) as Registro) }
    },
    onSuccess: (copia) => {
      qc.invalidateQueries({ queryKey: ["cadastro-conteudo", esquema.chave] })
      qc.invalidateQueries({ queryKey: ["catalogo-conteudo"] })
      const id = copia.id != null ? Number(copia.id) : null
      aoAvisar(`${maiuscula(esquema.singular)} ${esquema.feminino ? "duplicada" : "duplicado"}` +
               `${id != null ? ` — a cópia é o id ${id}` : ""}. Ajuste o que for diferente e grave.`)
      aoDuplicar(id != null ? copia : null, id)
    },
    onError: (e) => aoAvisar(mensagem(e)),
  })

  const salvar = useMutation({
    mutationFn: async (dados: Registro) => {
      const { id, ...corpo } = dados
      const sufixo = esquema.porBase ? `?baseId=${base}` : ""
      if (registro) {
        await api.put(`${esquema.rota}/${id}${sufixo}`, corpo, foraDoProjeto())
        return null
      }
      // Na criação a base vai no corpo: é ela que decide em qual idioma a linha
      // nasce, e a API só aceita isso no POST.
      const r = await api.post(esquema.rota, esquema.porBase ? { ...corpo, baseId: base } : corpo,
                               foraDoProjeto())
      return (r.data ?? null) as Registro | null
    },
    onSuccess: (criado, dados) => {
      qc.invalidateQueries({ queryKey: ["cadastro-conteudo", esquema.chave] })
      qc.invalidateQueries({ queryKey: ["catalogo-conteudo"] })
      if (registro) {
        aoAvisar("Alteração gravada.")
      } else {
        const id = criado?.id != null ? Number(criado.id) : null
        const nome = String(criado?.[esquema.campos[0]!.chave] ?? dados[esquema.campos[0]!.chave] ?? "")
        aoAvisar(`${maiuscula(esquema.singular)}${nome ? ` “${nome}”` : ""} ` +
                 `${esquema.feminino ? "incluída" : "incluído"}${id != null ? ` — id ${id}` : ""}.`)
        aoIncluir(id)
      }
      aoFechar()
    },
    onError: (e) => aoAvisar(mensagem(e)),
  })

  const faltando = esquema.campos
    .filter((c) => c.obrigatorio && !String(valores[c.chave] ?? "").trim())
    .map((c) => c.rotulo)

  return (
    <aside style={{
      flex: "0 0 360px", width: 360, overflowY: "auto",
      borderLeft: "1px solid var(--line2)", background: "var(--surface)", padding: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <b style={{ fontSize: 13 }}>
          {registro ? `Alterar ${esquema.singular}` : `Novo ${esquema.singular}`}
        </b>
        {registro && (
          <span className="mono" style={{ fontSize: 11, color: "var(--txt3)" }}>
            {String(registro.id)}
          </span>
        )}
        <button
          onClick={aoFechar}
          aria-label="Fechar"
          style={{
            marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
            color: "var(--txt3)", fontSize: 16, lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {esquema.porBase && (
        <p style={{
          margin: "0 0 10px", padding: "6px 9px", borderRadius: 5, fontSize: 11,
          background: "var(--surface2)", color: "var(--txt2)", lineHeight: 1.5,
        }}>
          Este cadastro tem uma linha por idioma, e a chave inclui a base. Você está em{" "}
          <b>{base === 2 ? "espanhol" : "português"}</b> — {registro
            ? "gravar aqui não toca a outra versão, mesmo que o id seja o mesmo."
            : "o novo registro nasce nesta base."}
        </p>
      )}

      {esquema.campos.map((c) => (
        <CampoDoForm
          key={c.chave}
          campo={c}
          valor={valores[c.chave]}
          aoMudar={(v) => setValores((x) => ({ ...x, [c.chave]: v }))}
        />
      ))}

      {faltando.length > 0 && (
        <p style={{ margin: "10px 0 0", fontSize: 11.5, color: "var(--txt2)" }}>
          Falta preencher: {faltando.join(", ")}.
        </p>
      )}

      {/* So o produto tem isto: a descricao do conteudo e montada dos vinculos, e
          sai de sincronia quando alguem renomeia um veiculo ou um sistema. */}
      {esquema.chave === "produtos" && registro && (
        <RecalcularDescricao produtoId={Number(registro.id)} />
      )}

      {/* Categoria e metade da regra de acesso: sem ela, licenca nenhuma abre o
          conteudo do veiculo. Mora na ficha porque o alvo ja esta escolhido --
          a procura de quem esta errado e o aviso no topo da lista. */}
      {registro && esquema.chave === "veiculos" && (
        <AlcanceDoVeiculo veiculoId={Number(registro.id)} aoAvisar={aoAvisar} />
      )}

      {/* A foto que a busca mostra. O `key` zera o formulario ao trocar de
          veiculo -- sem ele, o arquivo escolhido para um iria para o outro. */}
      {registro && esquema.chave === "veiculos" && (
        <ImagensDoVeiculo
          key={String(registro.id)}
          veiculoId={Number(registro.id)}
          nomeDoVeiculo={String(registro[esquema.campos[0]!.chave] ?? "")}
          aoAvisar={aoAvisar}
        />
      )}

      {/* Juntar dois cadastros que sao a mesma coisa. So veiculo e motor tem a
          rota, e e a operacao mais perigosa do sistema -- por isso mora aqui,
          onde o alvo esta na frente, e nao numa tela de manutencao que pede id. */}
      {registro && (esquema.chave === "veiculos" || esquema.chave === "motores") && (
        <MigrarCadastro
          tipo={esquema.chave === "veiculos" ? "veiculo" : "motor"}
          origemId={Number(registro.id)}
          origemNome={String(registro[esquema.campos[0]!.chave] ?? registro.id)}
          aoAvisar={aoAvisar}
        />
      )}

      {/* Presos ao pé da ficha: em motor e veículo a ficha passa da altura da
          tela, e com o Gravar fora de vista a pessoa ia no "Incluir" lá de cima. */}
      <div style={{
        position: "sticky", bottom: -14, margin: "14px -14px -14px", padding: "10px 14px 14px",
        background: "var(--surface)", borderTop: "1px solid var(--line2)",
      }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Botao
            aoClicar={() => { aoAvisar(null); salvar.mutate(valores) }}
            destaque
            desligado={faltando.length > 0 || duplicar.isPending}
            ocupado={salvar.isPending}
          >
            {salvar.isPending ? "Gravando…" : "Gravar"}
          </Botao>
          {registro && (
            <Botao aoClicar={() => setConfirmando("duplicar")}
                   desligado={faltando.length > 0 || salvar.isPending}
                   ocupado={duplicar.isPending}>
              {duplicar.isPending ? "Duplicando…" : "Duplicar"}
            </Botao>
          )}
          {registro && (
            <Botao aoClicar={() => setConfirmando("excluir")}>Excluir</Botao>
          )}
        </div>

        {confirmando === "duplicar" && registro && (
          <div style={{
            marginTop: 12, padding: 10, borderRadius: 6,
            border: "1px solid var(--line)",
          }}>
            <p style={{ margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.5 }}>
              Duplicar <b>{String(registro[esquema.campos[0]!.chave] ?? registro.id)}</b>: grava{" "}
              {esquema.feminino ? "uma nova" : "um novo"} {esquema.singular} com o que está nesta ficha
              agora, e a cópia abre em seguida para você ajustar. {maiuscula(esteEsta(esquema))}{" "}
              {esquema.singular} fica como está — alteração não gravada aqui vai só para a cópia.
            </p>
            <div style={{ display: "flex", gap: 6 }}>
              <Botao aoClicar={() => { setConfirmando(null); aoAvisar(null); duplicar.mutate(valores) }} destaque>
                Confirmo, duplicar
              </Botao>
              <Botao aoClicar={() => setConfirmando(null)}>Cancelar</Botao>
            </div>
          </div>
        )}

        {confirmando === "excluir" && registro && (
          <div style={{
            marginTop: 12, padding: 10, borderRadius: 6,
            border: "1px solid var(--erro)",
          }}>
            <p style={{ margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.5 }}>
              Excluir <b>{String(registro[esquema.campos[0]!.chave] ?? registro.id)}</b>.
              Isto é dado global: se houver conteúdo usando {esteEsta(esquema)} {esquema.singular}, ele fica
              apontando para um registro que não existe mais — e isso só aparece depois, no site.
            </p>

            {/* O motor é o único destes cadastros em que dá para responder "quanto",
                e não só "se houver": a API conta as sete tabelas que apontam para ele. */}
            {esquema.chave === "motores" && <UsoDoMotor motorId={Number(registro.id)} />}
            <div style={{ display: "flex", gap: 6 }}>
              <Botao aoClicar={() => aoExcluir(Number(registro.id))} ocupado={excluindo}>
                {excluindo ? "Excluindo…" : "Confirmo, excluir"}
              </Botao>
              <Botao aoClicar={() => setConfirmando(null)}>Cancelar</Botao>
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}

/**
 * Recalcular a descrição dos conteúdos do produto.
 *
 * A descrição é marca + veículo + motor + sistema + fabricante + nome do sistema
 * + assunto, e sai de sincronia quando alguém renomeia uma dessas coisas.
 *
 * **Conferir é o padrão, e aqui isso não é formalidade.** A regra não devolve o
 * que está gravado: as descrições do acervo trazem faixa de ano e acabamento,
 * que não saem de vínculo nenhum. No produto 26, 1.008 dos 1.026 mudariam e 410
 * **ficariam mais curtos**. Aplicar em massa empobrece o catálogo, e a tela põe
 * esse número na frente do botão.
 *
 * **E o "aplicar" do sistema antigo grava o id no lugar da descrição**, por um
 * `cont == 1` repetido onde devia ser `cont == 3`. Nenhum conteúdo em produção
 * tem descrição só numérica — a tela nunca foi usada para valer.
 */
function RecalcularDescricao({ produtoId }: { produtoId: number }) {
  const [previa, setPrevia] = useState<RecalculoDeDescricao | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const conferir = useMutation({
    mutationFn: (aplicar: boolean) => recalcularDescricao({ produtoId, aplicar }),
    onSuccess: setPrevia,
    onError: (e) => { setPrevia(null); setErro(mensagem(e)) },
  })

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line2)" }}>
      <h3 style={{
        margin: "0 0 5px", fontSize: 10, fontWeight: 600, letterSpacing: ".06em",
        textTransform: "uppercase", color: "var(--acao)",
      }}>
        Descrição do conteúdo
      </h3>
      <p style={{ margin: "0 0 8px", fontSize: 11, color: "var(--txt2)", lineHeight: 1.5 }}>
        Remonta a descrição a partir dos vínculos. <b>Confira antes</b>: a regra costuma
        encurtar o texto, porque não sabe produzir a faixa de ano nem o acabamento.
      </p>

      <Botao aoClicar={() => { setErro(null); conferir.mutate(false) }} ocupado={conferir.isPending}>
        {conferir.isPending ? "Conferindo…" : "Conferir o que mudaria"}
      </Botao>

      {erro && <p style={{ margin: "8px 0 0", fontSize: 11.5, color: "var(--erro)" }}>{erro}</p>}

      {previa && (
        <div style={{ marginTop: 10, fontSize: 11.5, lineHeight: 1.6 }}>
          {previa.aplicado && (
            <p style={{ margin: "0 0 6px", color: "var(--ok)" }}>
              Aplicado em {previa.alterados} conteúdo(s).
            </p>
          )}
          <p style={{ margin: "0 0 6px" }}>
            <b className="num">{previa.conteudos}</b> conteúdo(s):{" "}
            <b className="num">{previa.vaoMudar}</b> mudariam,{" "}
            <span className="num">{previa.jaEstaoAssim}</span> já estão assim,{" "}
            <span className="num">{previa.ficariamVazios}</span> ficariam vazios (não são tocados).
          </p>
          {previa.perderiamTexto > 0 && (
            <p style={{ margin: "0 0 6px", color: "var(--erro)" }}>
              <b className="num">{previa.perderiamTexto}</b> ficariam <b>mais curtos</b>.
            </p>
          )}
          {previa.avisos.map((a, i) => (
            <p key={i} style={{ margin: "0 0 6px", color: "var(--txt2)" }}>{a}</p>
          ))}

          {previa.mudancas.length > 0 && (
            <div style={{
              maxHeight: 200, overflowY: "auto", marginTop: 6, padding: 8,
              border: "1px solid var(--line2)", borderRadius: 6, background: "var(--surface3)",
            }}>
              {previa.mudancas.slice(0, 20).map((m) => (
                <div key={`${m.dadoId}-${m.baseId}`} style={{ marginBottom: 7 }}>
                  <div style={{ fontSize: 10.5, color: "var(--txt3)" }}>#{m.dadoId} · base {m.baseId}</div>
                  <div style={{ color: "var(--txt2)" }}>− {m.atual}</div>
                  <div>+ {m.proposta}</div>
                </div>
              ))}
            </div>
          )}

          {!previa.aplicado && previa.vaoMudar > 0 && (
            <div style={{ marginTop: 8 }}>
              <Botao aoClicar={() => conferir.mutate(true)} ocupado={conferir.isPending}>
                {previa.perderiamTexto > 0
                  ? `Aplicar mesmo encurtando ${previa.perderiamTexto}`
                  : `Aplicar nos ${previa.vaoMudar}`}
              </Botao>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Onde este motor é usado — as sete tabelas que apontam para ele.
 *
 * **Não há chave estrangeira em nenhuma delas**: apagar não cascateia nem
 * reclama, só deixa `motor_id` apontando para o vazio. São 1.395 motores, média
 * de 492 usos, e 342 que ninguém usa — esses saem sem consequência, e é a
 * diferença que esta caixa existe para mostrar.
 */
function UsoDoMotor({ motorId }: { motorId: number }) {
  const uso = useQuery({
    queryKey: ["motor-uso", motorId] as const,
    queryFn: () => obter<{
      total: number
      tabelaPesquisa: number
      modelos: number
      modelosCorreias: number
      planosManutencao: number
      relesFusiveis: number
      tabelasEspecificacao: number
      torquesAperto: number
    }>(`api/motores/${motorId}/uso`, {}),
    enabled: motorId > 0,
  })

  if (uso.isPending) {
    return <p style={{ margin: "0 0 8px", fontSize: 11, color: "var(--txt3)" }}>Contando o uso…</p>
  }
  if (uso.error || !uso.data) return null

  const d = uso.data
  const partes: [string, number][] = [
    ["conteúdo", d.tabelaPesquisa],
    ["modelos", d.modelos],
    ["correias", d.modelosCorreias],
    ["planos", d.planosManutencao],
    ["relés", d.relesFusiveis],
    ["especificações", d.tabelasEspecificacao],
    ["torques", d.torquesAperto],
  ]

  if (d.total === 0) {
    return (
      <p style={{ margin: "0 0 8px", fontSize: 11.5, color: "var(--ok)" }}>
        <b>Ninguém usa este motor.</b> São 342 assim no acervo — este sai sem consequência.
      </p>
    )
  }

  return (
    <div style={{ margin: "0 0 8px" }}>
      <p style={{ margin: "0 0 4px", fontSize: 11.5, color: "var(--erro)" }}>
        <b>{d.total.toLocaleString("pt-BR")} registro(s) apontam para este motor</b> e vão ficar
        apontando para o vazio:
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, lineHeight: 1.6, color: "var(--txt2)" }}>
        {partes.filter(([, n]) => n > 0).map(([rotulo, n]) => (
          <li key={rotulo}>
            <span className="num">{n.toLocaleString("pt-BR")}</span> em {rotulo}
          </li>
        ))}
      </ul>
    </div>
  )
}

function CampoDoForm({ campo, valor, aoMudar }: {
  campo: Campo
  valor: unknown
  aoMudar: (v: unknown) => void
}) {
  const referencia = useQuery({
    queryKey: ["catalogo-conteudo", campo.referencia] as const,
    enabled: campo.tipo === "referencia",
    ...catalogoPorProjeto,
    queryFn: () => obter<Registro[]>(`api/${campo.referencia}`, {}),
  })

  return (
    <div style={{ marginBottom: 9 }}>
      <label style={{ display: "block", fontSize: 10.5, color: "var(--txt2)", marginBottom: 3 }}>
        {campo.rotulo}
        {campo.obrigatorio && <span style={{ color: "var(--erro)" }}> *</span>}
      </label>

      {campo.tipo === "booleano" ? (
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <input type="checkbox" checked={Boolean(valor)} onChange={(e) => aoMudar(e.target.checked)} />
          {valor ? "sim" : "não"}
        </label>
      ) : campo.tipo === "referencia" ? (
        <select
          value={Number(valor ?? 0)}
          onChange={(e) => aoMudar(Number(e.target.value))}
          style={estilo}
        >
          <option value={0}>{referencia.isPending ? "Carregando…" : "Escolha…"}</option>
          {(referencia.data ?? [])
            .filter((x) => Number(x.id) > 0)
            .slice(0, 400)
            .map((x) => (
              <option key={String(x.id)} value={Number(x.id)}>
                {String(x.nome ?? x.modelo ?? x.descricao ?? x.id)}
              </option>
            ))}
        </select>
      ) : (
        <input
          type={campo.tipo === "numero" ? "number" : "text"}
          value={campo.tipo === "numero" ? (Number(valor) || "") : String(valor ?? "")}
          onChange={(e) => aoMudar(campo.tipo === "numero" ? Number(e.target.value) : e.target.value)}
          style={estilo}
        />
      )}

      {campo.ajuda && (
        <p style={{ margin: "2px 0 0", fontSize: 10, color: "var(--txt3)", lineHeight: 1.45 }}>
          {campo.ajuda}
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- peças

function textoDe(v: unknown, c: Campo, nomes: Map<string, Map<number, string>>): string {
  if (c.tipo === "booleano") return v ? "sim" : "não"
  if (v === null || v === undefined || v === "") return "—"
  if (c.tipo === "referencia" && c.referencia) {
    // Um id que não está no catálogo é registro apagado que alguém ainda cita:
    // mostrar o número é mais útil que um traço, porque dá para investigar.
    return nomes.get(c.referencia)?.get(Number(v)) ?? `#${v}`
  }
  return String(v)
}

function maiuscula(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function mensagem(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return "Não foi possível concluir."
}

function Botao({ children, aoClicar, destaque, desligado, ocupado }: {
  children: React.ReactNode
  aoClicar: () => void
  destaque?: boolean
  desligado?: boolean
  ocupado?: boolean
}) {
  const inativo = desligado || ocupado
  return (
    <button
      onClick={aoClicar}
      disabled={inativo}
      style={{
        border: "1px solid " + (destaque && !inativo ? "var(--acao)" : "var(--line)"),
        borderRadius: 6, padding: "4px 11px", fontSize: 12,
        cursor: inativo ? "default" : "pointer",
        background: destaque && !inativo ? "var(--acao)" : "var(--surface)",
        color: destaque && !inativo ? "#fff" : inativo ? "var(--txt3)" : "var(--txt)",
      }}
    >
      {children}
    </button>
  )
}

const th: React.CSSProperties = {
  textAlign: "left", padding: "6px 8px", fontSize: 11, fontWeight: 600,
  color: "var(--txt2)", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap",
}
const td: React.CSSProperties = { padding: "5px 8px", verticalAlign: "top" }
const estilo: React.CSSProperties = {
  width: "100%", padding: "5px 7px", fontSize: 12,
  border: "1px solid var(--line)", borderRadius: 5,
  background: "var(--surface)", color: "var(--txt)",
}
